import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import DashboardPage from "@/pages/DashboardPage";
import KeywordsPage from "@/pages/KeywordsPage";
import GroupsPage from "@/pages/GroupsPage";
import BroadcastPage from "@/pages/BroadcastPage";
import HolidaysPage from "@/pages/HolidaysPage";
import CampaignsPage from "@/pages/CampaignsPage";
import AIBudgetPage from "@/pages/AIBudgetPage";
import GrokBotPage from "@/pages/GrokBotPage";
import SettingsPage from "@/pages/SettingsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import ConnectionsPage from "@/pages/ConnectionsPage";
import HelpPage from "@/pages/HelpPage";
import LoginPage from "@/pages/LoginPage";
import UsersPage from "@/pages/UsersPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="p-8 text-sm text-zinc-400">Loading…</div>;
  // Always require auth — single-user private app, no auto-entry.
  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  }
  // Force password change if PUK / temporary password is set
  if (user?.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/change-password" element={<RequireAuth><ChangePasswordPage /></RequireAuth>} />
      <Route path="/" element={<RequireAuth><DashboardPage /></RequireAuth>} />
      <Route path="/keywords" element={<RequireAuth><KeywordsPage /></RequireAuth>} />
      <Route path="/groups" element={<RequireAuth><GroupsPage /></RequireAuth>} />
      <Route path="/broadcast" element={<RequireAuth><BroadcastPage /></RequireAuth>} />
      <Route path="/holidays" element={<RequireAuth><HolidaysPage /></RequireAuth>} />
      <Route path="/campaigns" element={<RequireAuth><CampaignsPage /></RequireAuth>} />
      <Route path="/ai-budget" element={<RequireAuth><AIBudgetPage /></RequireAuth>} />
      <Route path="/grok-bot" element={<RequireAuth><GrokBotPage /></RequireAuth>} />
      <Route path="/notifications" element={<RequireAuth><NotificationsPage /></RequireAuth>} />
      <Route path="/connections" element={<RequireAuth><ConnectionsPage /></RequireAuth>} />
      <Route path="/settings" element={<RequireAuth><SettingsPage /></RequireAuth>} />
      <Route path="/users" element={<RequireAuth><UsersPage /></RequireAuth>} />
      <Route path="/help" element={<HelpPage />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            borderRadius: 0,
            border: "1px solid #e4e4e7",
            background: "#fff",
            color: "#18181b",
            fontFamily: "IBM Plex Sans, sans-serif",
          },
        }}
      />
    </div>
  );
}

export default App;
